import axios from "axios";
import {
  ArrowRight,
  CheckCircle,
  Heart,
  Minus,
  PenLine,
  Plus,
  ShoppingCart,
  Star,
  Zap,
} from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/customer/shop/subscreens/pd_ProductInfo.css";
import ToastNotification from "../../../components/customer/shop/ToastNotification";
import AppContext from "../../../context/AppContext";

const ProductInfo = ({ product }) => {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [toast, setToast] = useState(null);
  const { baseUrl, authToken, baseUrlForFile, setCartCount, loggedInCustomer } =
    useContext(AppContext);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    const existingWishlist = JSON.parse(
      localStorage.getItem("wishlist") || "[]",
    );
    const isInWishlist = existingWishlist.some(
      (item) => item.id === product.id,
    );
    setIsWishlisted(isInWishlist);
  }, [product.id]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "PKR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleQuantityChange = (action) => {
    if (action === "increase") {
      setQuantity((prev) => prev + 1);
    } else if (action === "decrease" && quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const addToCart = (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    setIsAdding(true);

    const formdata = new FormData();
    formdata.append("adcustome_id", loggedInCustomer?.id);
    formdata.append("adproduct_id", product.id);
    formdata.append("price", product.price);
    formdata.append("total_price", product.price);
    formdata.append("quantity", 1);
    formdata.append("type", "normal");

    axios
      .post(`${baseUrl}/customer/cart/createCartItem`, formdata, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setIsAdding(false);

        if (response.data.success) {
          setCartCount(response.data.cartCount);
          setToast({
            message: `${quantity}x ${product.name} added to cart successfully!`,
            type: "cart",
          });
        }
      })
      .catch((error) => {
        console.log("Cart Error:", error);
        setIsAdding(false);
      });
  };

  const handleAddToCart = () => {
    const existingCart = JSON.parse(localStorage.getItem("cart") || "[]");
    const existingItemIndex = existingCart.findIndex(
      (item) => item.id === product.id,
    );

    if (existingItemIndex !== -1) {
      existingCart[existingItemIndex].quantity += quantity;
    } else {
      existingCart.push({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity: quantity,
        shortDesc: product.shortDesc,
      });
    }

    localStorage.setItem("cart", JSON.stringify(existingCart));
    setToast({
      message: `${quantity}x ${product.name} added to cart successfully!`,
      type: "cart",
    });
  };

  const handleBuyNow = () => {
    handleAddToCart();

    setTimeout(() => {
      setToast({
        message: `Redirecting to checkout for ${product.name}...`,
        type: "buy",
      });
    }, 500);
    setTimeout(() => {
      navigate("/checkout");
    }, 1500);
  };

  const toggleWishlist = () => {
    const newWishlistState = !isWishlisted;
    setIsWishlisted(newWishlistState);
    const existingWishlist = JSON.parse(
      localStorage.getItem("wishlist") || "[]",
    );

    if (newWishlistState) {
      if (!existingWishlist.find((item) => item.id === product.id)) {
        existingWishlist.push({
          id: product.id,
          name: product.name,
          price: product.price,
          originalPrice: product.originalPrice,
          image: product.image,
          discount: product.discount,
          rating: product.rating,
          inStock: product.inStock,
        });
        localStorage.setItem("wishlist", JSON.stringify(existingWishlist));
        setToast({
          message: `${product.name} added to wishlist! ❤️`,
          type: "wishlist",
        });
      }
    } else {
      const updatedWishlist = existingWishlist.filter(
        (item) => item.id !== product.id,
      );
      localStorage.setItem("wishlist", JSON.stringify(updatedWishlist));
      setToast({
        message: `${product.name} removed from wishlist`,
        type: "success",
      });
    }
  };

  return (
    <>
      {toast && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="pd-product-info">
        {/* Category & Tags */}
        <div className="pd-meta">
          <span className="pd-category">
            {product.category?.replace("-", " ") || "Furniture"}
          </span>
          {product.tags && product.tags.length > 0 && (
            <div className="pd-tags">
              {product.tags.map((tag, index) => (
                <span key={index} className="pd-tag">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Product Name */}
        <h1 className="pd-product-name">{product.name}</h1>

        {/* Short Description */}
        {product.shortDesc && (
          <p className="pd-short-desc">{product.shortDesc}</p>
        )}

        {/* Rating & Reviews */}
        <div className="pd-rating-section">
          <div className="pd-stars">
            {[...Array(5)].map((_, index) => (
              <Star
                key={index}
                size={22}
                className={`pd-star ${index < Math.floor(product.rating) ? "pd-star-filled" : ""}`}
                fill={
                  index < Math.floor(product.rating) ? "currentColor" : "none"
                }
                stroke="currentColor"
              />
            ))}
          </div>
          <span className="pd-rating-text">
            {product.rating} ({product.reviews} reviews)
          </span>
        </div>

        {/* Price Section */}
        <div className="pd-price-section">
          <div className="pd-price-main">
            <span className="pd-price-current">
              {formatPrice(product.price)}
            </span>
            {product.originalPrice && (
              <span className="pd-price-original">
                {formatPrice(product.originalPrice)}
              </span>
            )}
          </div>
          {product.discount > 0 && (
            <span className="pd-discount-badge">Save {product.discount}%</span>
          )}
        </div>

        {/* Divider */}
        <div className="pd-divider"></div>

        {/* Quantity Selector & Actions */}
        {/* {product.inStock && ( */}
        <div className="pd-actions-section">
          {/* Quantity Selector */}
          <div className="pd-quantity-selector">
            <label className="pd-quantity-label">Quantity:</label>
            <div className="pd-quantity-controls">
              <button
                className="pd-qty-btn"
                onClick={() => handleQuantityChange("decrease")}
                disabled={quantity === 1}
              >
                <Minus size={20} />
              </button>
              <span className="pd-qty-value">{quantity}</span>
              <button
                className="pd-qty-btn"
                onClick={() => handleQuantityChange("increase")}
              >
                <Plus size={20} />
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pd-action-buttons">
            <button
              className="pd-add-cart-btn" //{`${isAdding ? "pd-adding" : ""}`}
              onClick={addToCart}
            >
              <ShoppingCart size={22} />
              Add to Cart
            </button>

            <button
              className={`pd-wishlist-btn ${isWishlisted ? "pd-wishlisted" : ""}`}
              onClick={toggleWishlist}
              aria-label="Add to wishlist"
            >
              <Heart
                size={26}
                fill={isWishlisted ? "currentColor" : "none"}
                stroke="currentColor"
              />
            </button>
          </div>

          {/* ── Customize Button ── */}
          <div className="pd-customize-wrap">
            <div className="pd-customize-or">
              <span>or</span>
            </div>
            <button
              className="pd-customize-btn"
              onClick={() =>
                navigate(`/product/custom-product`, { state: { product } })
              }
              type="button"
            >
              <PenLine size={17} />
              <span>Build It Your Way</span>
              <ArrowRight size={14} />
            </button>
            <p className="pd-customize-note">
              <CheckCircle size={11} />
              Free to request · No payment required at this stage
            </p>
          </div>
        </div>
        {/* )} */}

        {/* Divider */}
        <div className="pd-divider"></div>

        {/* Product Specifications */}
        <div className="pd-specifications">
          <h3 className="pd-spec-title">Specifications</h3>
          <div className="pd-spec-list">
            {product.dimensions && (
              <div className="pd-spec-item">
                <span className="pd-spec-label">Dimensions:</span>
                <span className="pd-spec-value">{product.dimensions}</span>
              </div>
            )}
            {product.material && (
              <div className="pd-spec-item">
                <span className="pd-spec-label">Material:</span>
                <span className="pd-spec-value">{product.material}</span>
              </div>
            )}
            {product.color && (
              <div className="pd-spec-item">
                <span className="pd-spec-label">Color:</span>
                <span className="pd-spec-value">{product.color}</span>
              </div>
            )}
            {product.seating && (
              <div className="pd-spec-item">
                <span className="pd-spec-label">Seating:</span>
                <span className="pd-spec-value">{product.seating}</span>
              </div>
            )}
            {product.features && (
              <div className="pd-spec-item">
                <span className="pd-spec-label">Features:</span>
                <span className="pd-spec-value">{product.features}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductInfo;
